import type { OcrRow } from '../ocr/types.js';
import type { Receipt } from '../types.js';
import type { SplitContext } from '../split/dsl.js';
import type { SplitReply } from '../split/reply.js';
import type { SplitHistory } from './prompts.js';

/**
 * What a provider is given to turn into a receipt.
 *
 * Two shapes because the right one depends on the model. A small on-device
 * model gets `rows` — text already read and geometrically paired by stage 3,
 * because asking it to read pixels is asking too much. A frontier cloud model
 * gets `image` and reads the photo itself, which skips OCR entirely.
 */
export type ReceiptInput =
  | { kind: 'rows'; rows: OcrRow[] }
  | { kind: 'image'; base64: string; mimeType: string };

/**
 * The only surface through which the app talks to a model.
 *
 * Both calls sit behind this interface so the runtime is a one-file swap.
 * Nothing above this line knows whether inference happened on the device, at
 * the edge, or in a datacentre — or which vendor served it.
 */
export interface InferenceProvider {
  readonly kind: 'ondevice' | 'cloud' | 'mock';

  /** A photo or OCR rows → structured receipt. The hard task. */
  structureReceipt(input: ReceiptInput, signal?: AbortSignal): Promise<Receipt>;

  /**
   * A sentence → operations over the given IDs. The easy task, and the
   * differentiating one — this is the call that stays on-device even if
   * receipt structuring has to fall back to the cloud.
   */
  parseSplit(
    context: SplitContext,
    utterance: string,
    signal?: AbortSignal,
    history?: SplitHistory,
  ): Promise<SplitReply>;
}

export class InferenceError extends Error {
  constructor(
    message: string,
    readonly details: string[] = [],
  ) {
    super(message);
    this.name = 'InferenceError';
  }
}
